import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, imgUrl } from "../../lib/api";
import { useLang } from "../../i18n";

const EMPTY = {
  name_it: "", name_en: "", desc_it: "", desc_en: "", category: "mieli", botanical: "", image: "",
  sizesText: "250g:9.50", stock: 100, featured: false, harvest: "", batch: "", pairings_it: "", pairings_en: "",
};

export default function ProductForm() {
  const { t } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(!!id);

  useEffect(() => {
    if (!id) return;
    api.get(`/products/${id}`).then((r) => {
      setForm({ ...EMPTY, ...r.data, sizesText: r.data.sizes.map((s) => `${s.label}:${s.price}`).join(",") });
    }).catch(() => toast.error("Prodotto non trovato")).finally(() => setLoading(false));
  }, [id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.path }));
      toast.success("Foto caricata ✓");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const sizes = form.sizesText.split(",").map((s) => {
        const [label, price] = s.split(":");
        return { label: label.trim(), price: parseFloat(price) };
      }).filter((s) => s.label && !isNaN(s.price));
      if (sizes.length === 0) throw new Error("Formati non validi");
      const payload = {
        name_it: form.name_it, name_en: form.name_en || form.name_it,
        desc_it: form.desc_it, desc_en: form.desc_en, category: form.category,
        botanical: form.botanical, image: form.image, sizes, stock: Number(form.stock),
        featured: !!form.featured, harvest: form.harvest, batch: form.batch,
        pairings_it: form.pairings_it, pairings_en: form.pairings_en,
      };
      if (id) await api.put(`/admin/products/${id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("✓");
      navigate("/admin/prodotti");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inputCls = "w-full bg-obsidian border border-slate-700 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-gold";
  const labelCls = "block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1.5";

  if (loading) return <p className="font-mono text-sm text-slate-400">{t("common.loading")}</p>;

  return (
    <div className="max-w-3xl" data-testid="product-form-page">
      <Link to="/admin/prodotti" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold transition-colors" data-testid="pf-back-link">
        <ArrowLeft size={14} /> {t("admin.products")}
      </Link>
      <h1 className="mt-4 font-serif text-3xl font-bold" data-testid="product-form-title">{id ? t("admin.edit") : t("admin.new.product")}</h1>
      <form onSubmit={save} className="mt-8 bg-slate2 border border-slate-700 rounded-lg p-7 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
        <div>
          <label className={labelCls}>Nome (IT) *</label>
          <input required value={form.name_it} onChange={set("name_it")} placeholder="Es. Miele di Acacia" data-testid="pf-name-it" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Name (EN)</label>
          <input value={form.name_en} onChange={set("name_en")} placeholder="E.g. Acacia Honey" data-testid="pf-name-en" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Descrizione (IT)</label>
          <textarea value={form.desc_it} onChange={set("desc_it")} rows={2} data-testid="pf-desc-it" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Description (EN)</label>
          <textarea value={form.desc_en} onChange={set("desc_en")} rows={2} data-testid="pf-desc-en" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Categoria</label>
          <select value={form.category} onChange={set("category")} data-testid="pf-category" className={inputCls}>
            {["mieli", "polline", "propoli", "regalo"].map((c) => <option key={c} value={c}>{t(`cat.${c}`)}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Origine botanica</label>
          <input value={form.botanical} onChange={set("botanical")} placeholder="Es. Robinia pseudoacacia" data-testid="pf-botanical" className={inputCls} />
        </div>
        <div className="sm:col-span-2 space-y-2">
          <label className={labelCls}>Foto prodotto</label>
          <div className="flex items-center gap-4">
            {form.image && <img src={imgUrl(form.image)} alt="" className="w-16 h-16 object-cover rounded border border-slate-700" data-testid="pf-image-preview" />}
            <label className="flex-1 flex items-center justify-center gap-2 border border-dashed border-slate-600 px-4 py-3 text-sm text-slate-300 cursor-pointer hover:border-gold hover:text-gold transition-colors" data-testid="pf-upload-label">
              <Plus size={15} /> {uploading ? "Caricamento…" : "Carica foto dal tuo computer"}
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="pf-image-file" />
            </label>
          </div>
          <input value={form.image} onChange={set("image")} placeholder="oppure incolla URL immagine" data-testid="pf-image" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Formati e prezzi *</label>
          <input value={form.sizesText} onChange={set("sizesText")} placeholder="250g:9.50,500g:16.00" data-testid="pf-sizes" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Giacenza (stock)</label>
          <input type="number" value={form.stock} onChange={set("stock")} data-testid="pf-stock" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Raccolta</label>
          <input value={form.harvest} onChange={set("harvest")} placeholder="Es. Maggio 2026" data-testid="pf-harvest" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Lotto</label>
          <input value={form.batch} onChange={set("batch")} placeholder="Es. 2026-04-AC" data-testid="pf-batch" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Abbinamenti (IT)</label>
          <input value={form.pairings_it} onChange={set("pairings_it")} placeholder="Es. Tè, yogurt, formaggi freschi" data-testid="pf-pairings-it" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Pairings (EN)</label>
          <input value={form.pairings_en} onChange={set("pairings_en")} placeholder="E.g. Tea, yogurt, fresh cheeses" data-testid="pf-pairings-en" className={inputCls} />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={form.featured} onChange={set("featured")} data-testid="pf-featured" className="accent-amber-500" /> In evidenza
        </label>
        <div className="sm:col-span-2 flex gap-3 pt-2">
          <button type="submit" data-testid="pf-save" className="flex-1 bg-gold text-obsidian py-3 font-semibold hover:bg-honey transition-colors">{t("admin.save")}</button>
          <Link to="/admin/prodotti" data-testid="pf-cancel" className="px-6 py-3 border border-slate-600 text-slate-300 hover:border-slate-400 transition-colors text-center">{t("admin.cancel")}</Link>
        </div>
      </form>
    </div>
  );
}
